import { Injectable } from '@angular/core';
import moment from 'moment/moment';
import { combineLatest, map, Observable } from 'rxjs';

import { LoadingStatus } from '../../../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { DietitianStateProvider } from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { ScheduledTaskCommands } from '../../../../../../data/dietitian/domain/scheduled_task/scheduled_task_commands';
import { InvalidScheduledTaskDateException } from '../../../../../../data/dietitian/domain/scheduled_task/scheduled_task_exceptions';
import { ConversationProps } from '../../../../../../data/message/domain/conversation/conversation';
import { ConversationCommands } from '../../../../../../data/message/domain/conversation/conversation_commands';
import { MessageProps } from '../../../../../../data/message/domain/message/message';
import { MessageCommands } from '../../../../../../data/message/domain/message/message_commands';
import { Patient } from '../../../../../../data/patient/domain/patient';
import { PatientCommands } from '../../../../../../data/patient/domain/patient_commands';
import { PatientStateProvider } from '../../../../../../data/patient/domain/patient_state_provider';

export interface PatientInfoViewModel {
  isLoading: boolean;
  error: string;
  patient: Patient;
  dietitian: Dietitian;
}

@Injectable()
export class SendQuestionModalFacade {
  isLoading = false;

  constructor(
    private patientStateProvider: PatientStateProvider,
    private dietitianStateProvider: DietitianStateProvider,
    private messageCommands: MessageCommands,
    private conversationCommands: ConversationCommands,
    private patientCommands: PatientCommands,
    private scheduledTaskCommands: ScheduledTaskCommands,
  ) {
    // do nothing
  }

  get viewModel$(): Observable<PatientInfoViewModel> {
    return combineLatest({
      events: combineLatest({
        dietitianState: this.dietitianStateProvider.state$,
        patientState: this.patientStateProvider.state$,
      }),
    }).pipe(
      map((e) => {
        // update view model
        let error: string | undefined;

        if (e.events.dietitianState.loading == LoadingStatus.ERROR) {
          error = e.events.dietitianState.message;
        }
        if (e.events.patientState.loading == LoadingStatus.ERROR) {
          error = e.events.patientState.message;
        }

        return <PatientInfoViewModel>{
          isLoading:
            e.events.dietitianState.loading == LoadingStatus.LOADING ||
            e.events.patientState.loading == LoadingStatus.LOADING,
          error,
          dietitian: e.events.dietitianState.entity,
          patient: e.events.patientState.entity,
        };
      }),
    );
  }

  sendMessagesToGroupedPatients(
    subject: string,
    message: string,
    patientGroup: PatientGroup | undefined,
    archived: boolean,
  ): Promise<void> {
    return this.conversationCommands.sendMessagesToGroupedPatients(
      subject,
      message,
      patientGroup,
      archived,
    );
  }

  sendMessagesToPatientSelection(
    subject: string,
    message: string,
    patients: Patient[],
  ) {
    return this.conversationCommands.sendMessagesToPatientSelection(
      subject,
      message,
      patients,
    );
  }

  getPatient(patientId: string) {
    return this.patientCommands.getPatient(patientId);
  }

  async sendQuestion(props: ConversationProps, message: string) {
    if (props.patient) {
      const createdMessage = await this.conversationCommands.save(props);

      const messageProps = {
        message,
        recipientId: props.patient.profileId.id,
        senderId: props.dietitian.profileId.id,
        createdAt: new Date(),
      } as MessageProps;

      return this.messageCommands.save(
        createdMessage.conversationId.id.toString(),
        messageProps,
      );
    } else {
      throw new Error('Patient destinataire invalide');
    }
  }

  async scheduleMessagesToPatientSelection(
    dietitian: Dietitian,
    subject: string,
    message: string,
    target: Patient[],
    scheduledDate: Date,
    scheduledTime: string,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      await this.scheduledTaskCommands.scheduleMessage(
        dietitian,
        target,
        scheduledAt,
        subject,
        message,
        false,
      );
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  async scheduleQuestion(
    dietitian: Dietitian,
    props: ConversationProps,
    message: string,
    scheduledDate: Date,
    scheduledTime: string,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      if (props.patient) {
        await this.scheduledTaskCommands.scheduleMessage(
          dietitian,
          props.patient,
          scheduledAt,
          props.subject,
          message,
        );
      } else {
        throw new Error('Patient destinataire invalide');
      }
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  async scheduleMessagesToGroupedPatients(
    dietitian: Dietitian,
    subject: any,
    message: any,
    target: PatientGroup,
    scheduledDate: Date,
    scheduledTime: string,
    archived: boolean,
  ) {
    const scheduledAt = this.getScheduledDateTime(scheduledDate, scheduledTime);
    if (scheduledAt) {
      await this.scheduledTaskCommands.scheduleMessage(
        dietitian,
        target,
        scheduledAt,
        subject,
        message,
        archived,
      );
    } else {
      throw new InvalidScheduledTaskDateException();
    }
  }

  private getScheduledDateTime(
    scheduledDate: Date,
    scheduledTime: string,
  ): Date | undefined {
    let scheduledAt: moment.Moment | undefined;
    if (scheduledDate && scheduledTime) {
      // merging date and time
      scheduledAt = moment(scheduledDate).startOf('day');
      const time = moment(scheduledTime, 'HH:mm');
      scheduledAt.set({
        hours: time.hours(),
        minutes: time.minutes(),
      });
    }
    if (!scheduledAt || scheduledAt.toDate() < new Date()) {
      throw new InvalidScheduledTaskDateException();
    }
    return scheduledAt?.toDate();
  }
}
